import { useState } from "react";
import { Alert, Button, Checkbox, Group, Modal, MultiSelect, Stack, Text, TextInput } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import customerService from "@/services/customer";
import merchantUserService from "@/services/merchantUser";
import { assignOwners, unassignOwners } from "@/services/frontendFeatures";

export default function OwnerAssignment() {
  const { isManager } = useDashboardAccess();
  const [opened, setOpened] = useState(false);
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [members, setMembers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const client = useQueryClient();
  const customers = useQuery({ queryKey: ["customers-owner-selection", term, page], queryFn: () => customerService.getCustomers({ Term: term, Skip: page * 50, Count: 50 }), enabled: opened && isManager });
  const staff = useQuery({ queryKey: ["getMerchantUsers"], queryFn: merchantUserService.getMerchantUsers, enabled: opened && isManager });
  if (!isManager) return null;
  const run = async (remove: boolean) => {
    const ids = Object.keys(selected).map(Number);
    if (!ids.length || !members.length || members.length > 20 || busy) return;
    setBusy(true);
    setResult("");
    let processed = 0, changed = 0;
    try {
      for (let i = 0; i < ids.length; i += 500) {
        const body = { customerIds: ids.slice(i, i + 500), merchantUserIds: members.map(Number) };
        changed += remove ? (await unassignOwners(body)).removedCount : (await assignOwners(body)).assignedCount;
        processed += body.customerIds.length;
      }
      const message = remove ? `${changed} اختصاص برداشته شد` : changed ? `${changed} اختصاص انجام شد` : "این مشتریان از قبل اختصاص داشتند";
      setResult(message);
      notifications.show({ message, color: "green" });
    } catch {
      setResult(`عملیات متوقف شد؛ ${processed} مشتری در دسته‌های قبلی پردازش شدند. می‌توانید دوباره تلاش کنید.`);
      await Promise.all([customers.refetch(), staff.refetch()]);
    } finally {
      // Refresh affected caches even if a later batch failed.
      await Promise.all(["customers", "customersPagination", "customers-owner-selection", "getCustomerCount", "getCustomerCountChart", "merchant-dashboard-overview", "barber-dashboard"].map(key => client.invalidateQueries({ queryKey: [key] })));
      setBusy(false);
    }
  };
  return <><Group p="md"><Button variant="light" onClick={() => setOpened(true)}>اختصاص مشتری به پرسنل</Button></Group>
    <Modal opened={opened} onClose={() => !busy && setOpened(false)} title="اختصاص مشتری به پرسنل" size="lg">
      <Stack>
        <Alert>اختصاص‌های فعلی قابل مشاهده نیستند. افزودن، اختصاص‌های قبلی را حفظ می‌کند. پس از برداشتن، نوبت یا فاکتور بعدی می‌تواند اختصاص را دوباره ایجاد کند.</Alert>
        <MultiSelect label="پرسنل (حداکثر ۲۰ نفر)" searchable value={members} onChange={setMembers} maxValues={20} disabled={busy || staff.isLoading} data={(staff.data ?? []).filter(m => m.id != null).map(m => ({ value: String(m.id), label: `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim() || `پرسنل ${m.id}` }))} />
        {staff.isError && <Button onClick={() => void staff.refetch()}>دریافت دوباره پرسنل</Button>}
        <TextInput label="جستجوی مشتری" value={term} disabled={busy} onChange={e => { setTerm(e.currentTarget.value); setPage(0); }} />
        <Group><Text>{Object.keys(selected).length} مشتری انتخاب شده</Text><Button size="compact-xs" disabled={busy} onClick={() => setSelected({})}>پاک کردن انتخاب‌ها</Button></Group>
        {customers.isLoading ? <Text>در حال دریافت…</Text> : customers.isError ? <Button onClick={() => void customers.refetch()}>تلاش دوباره</Button> : <>
          <Button variant="subtle" disabled={busy} onClick={() => setSelected(prev => ({ ...prev, ...Object.fromEntries((customers.data?.result ?? []).filter(c => c.id != null).map(c => [c.id!, `${c.firstName ?? ""} ${c.lastName ?? ""}`])) }))}>انتخاب همهٔ این صفحه</Button>
          {(customers.data?.result ?? []).map(c => c.id == null ? null : <Checkbox key={c.id} disabled={busy} checked={c.id in selected} label={`${c.firstName ?? ""} ${c.lastName ?? ""} — ${c.phoneNumber ?? ""}`} onChange={e => { const checked = e.currentTarget.checked; setSelected(prev => { const next = { ...prev }; if (checked) next[c.id!] = c.phoneNumber ?? ""; else delete next[c.id!]; return next; }); }} />)}
          {!customers.data?.result?.length && <Text>مشتری یافت نشد</Text>}
          <Group><Button disabled={busy || page === 0} onClick={() => setPage(p => p - 1)}>قبلی</Button><Button disabled={busy || (customers.data?.result?.length ?? 0) < 50} onClick={() => setPage(p => p + 1)}>بعدی</Button></Group>
        </>}
        {result && <Alert>{result}</Alert>}
        <Group><Button loading={busy} disabled={!members.length || !Object.keys(selected).length} onClick={() => void run(false)}>افزودن اختصاص</Button><Button color="red" variant="light" disabled={busy || !members.length || !Object.keys(selected).length} onClick={() => void run(true)}>برداشتن اختصاص</Button></Group>
      </Stack>
    </Modal></>;
}


